import React, { useRef, useCallback, useState } from 'react';
import {
  Box,
  Textarea,
  IconButton,
  Flex,
  Tooltip,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
} from '@chakra-ui/react';
import { ArrowUpIcon } from '@chakra-ui/icons';
import { PlusIcon, Square } from 'lucide-react';

/**
 * Message input component with auto-resize textarea
 * Handles keyboard shortcuts: Enter to send, Shift+Enter for newline
 * 
 * @param {Object} props
 * @param {React.Ref} props.inputRef - Ref for the textarea element
 * @param {string} props.value - Current input value
 * @param {Function} props.onChange - onChange handler
 * @param {Function} props.onSend - Send message handler
 * @param {boolean} props.isLoading - Loading state
 * @param {boolean} props.isTextareaAtMaxHeight - Whether textarea is at max height
 * @param {boolean} props.isDisabled - Whether the input should be disabled (e.g., during operations)
 * @param {Function} props.onFileUpload - Callback when a file is selected for upload
 * @param {boolean} props.isUploading - Whether a file upload is in progress
 * @param {boolean} props.canUpload - Whether uploads are allowed (campaign must exist)
 */
const MessageInput = ({
  inputRef,
  value,
  onChange,
  onSend,
  isLoading,
  isGenerating = false,
  onStop,
  isTextareaAtMaxHeight,
  isDisabled = false,
  onFileUpload,
  isUploading = false,
  canUpload = false,
}) => {
  const localInputRef = useRef(null);
  const textareaRef = inputRef || localInputRef;
  const fileInputRef = useRef(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [uploadContext, setUploadContext] = useState('');

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
    // Shift+Enter allows new line (default behavior)
  }, [onSend]);

  const handlePlusClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setPendingFiles(files);
      setUploadContext('');
      setIsContextModalOpen(true);
    }
    // Reset so the same file(s) can be re-selected
    e.target.value = '';
  }, []);

  const closeContextModal = useCallback(() => {
    if (isUploading) return;
    setIsContextModalOpen(false);
    setPendingFiles([]);
    setUploadContext('');
  }, [isUploading]);

  const submitUploadWithContext = useCallback(() => {
    const prompt = uploadContext.trim();
    if (!prompt || pendingFiles.length === 0 || !onFileUpload) return;
    onFileUpload(pendingFiles, prompt);
    setIsContextModalOpen(false);
    setPendingFiles([]);
    setUploadContext('');
  }, [onFileUpload, pendingFiles, uploadContext]);

  const handleContextKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitUploadWithContext();
    }
    // Shift+Enter keeps newline behavior
  }, [submitUploadWithContext]);

  const uploadButtonDisabled = isUploading || isLoading || isDisabled;
  const showUploadButton = canUpload;

  const primaryButton = {
    bg: "#fafafa",
    color: "#0a0a0a",
    borderRadius: "full",
    fontWeight: "600",
    fontSize: "13px",
    h: "36px",
    _hover: { opacity: 0.85 },
  };

  const outlineButton = {
    bg: "transparent",
    borderRadius: "full",
    fontWeight: "500",
    fontSize: "13px",
    h: "36px",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "rgba(255,255,255,0.6)",
    _hover: { borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.9)", bg: "rgba(255,255,255,0.04)" },
  };

  return (
    <Box
      p={4}
      borderTop="1px solid"
      borderColor="rgba(255,255,255,0.10)"
      bg="#111111"
    >
      {/* Hidden file input — accepts CSV and common image types, multiple allowed */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".csv,image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/tiff"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Flex gap={2} align="flex-end">
        <Box flex={1} position="relative">
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            placeholder="Let's find some customers..."
            bg="#111111"
            border="1px solid"
            borderColor="rgba(255,255,255,0.08)"
            color="#fafafa"
            _placeholder={{ color: "#a3a3a3" }}
            _hover={{ borderColor: "rgba(255,255,255,0.15)" }}
            _focus={{ borderColor: "rgba(255,255,255,0.3)" }}
            borderRadius="10px"
            disabled={isLoading || isDisabled}
            resize="none"
            minH="40px"
            maxH="200px"
            overflowY="auto"
            fontSize="md"
            lineHeight="1.5"
            py={2}
            pl={showUploadButton ? 9 : 4}
            pr={4}
            css={isTextareaAtMaxHeight ? {
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.12)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(255,255,255,0.2)',
              },
            } : {
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { width: '0px', background: 'transparent' },
            }}
          />

          {/* Upload button — sits inside the textarea border at the bottom-left */}
          {showUploadButton && (
            <Tooltip
              label={isUploading ? 'Upload in progress…' : 'Upload a CSV or image'}
              placement="top"
              hasArrow={false}
              bg="#111111"
              color="#fafafa"
              borderRadius="6px"
              border="1px solid rgba(255,255,255,0.10)"
              py={1.5}
              px={2}
              fontSize="xs"
              fontWeight={400}
              boxShadow="sm"
            >
              <IconButton
                icon={
                  isUploading
                    ? <Spinner size="xs" color="rgba(255,255,255,0.5)" />
                    : <PlusIcon size={15} strokeWidth={1.5} />
                }
                onClick={handlePlusClick}
                isDisabled={uploadButtonDisabled}
                variant="ghost"
                size="xs"
                borderRadius="full"
                aria-label="Upload file"
                position="absolute"
                bottom="6px"
                left="6px"
                zIndex={1}
                color={isUploading ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.35)'}
                _hover={{ color: 'rgba(255,255,255,0.9)', bg: 'rgba(255,255,255,0.08)' }}
                _active={{ bg: 'rgba(255,255,255,0.04)' }}
                _focus={{ boxShadow: 'none' }}
                _focusVisible={{ boxShadow: 'none' }}
              />
            </Tooltip>
          )}
        </Box>
        {isGenerating ? (
          <IconButton
            icon={<Square size={16} fill="currentColor" strokeWidth={0} />}
            onClick={onStop}
            size="md"
            borderRadius="full"
            aria-label="Stop generation"
            flexShrink={0}
            bg="transparent"
            border="1px solid rgba(239,68,68,0.3)"
            color="rgba(239,68,68,0.7)"
            _hover={{ borderColor: "rgba(239,68,68,0.5)", color: "rgba(239,68,68,0.9)", bg: "rgba(239,68,68,0.06)" }}
            _active={{ bg: "rgba(239,68,68,0.06)" }}
            _focus={{ boxShadow: 'none' }}
            _focusVisible={{ boxShadow: 'none' }}
          />
        ) : (
          <IconButton
            icon={<ArrowUpIcon />}
            onClick={onSend}
            isDisabled={!value.trim() || isLoading || isDisabled}
            size="sm"
            borderRadius="full"
            aria-label="Send message"
            flexShrink={0}
            bg="#fafafa"
            color="#0a0a0a"
            _hover={{ opacity: 0.85 }}
            _disabled={{ opacity: 0.3, cursor: 'not-allowed' }}
            _focus={{ boxShadow: 'none' }}
            _focusVisible={{ boxShadow: 'none' }}
          />
        )}
      </Flex>

      <Modal isOpen={isContextModalOpen} onClose={closeContextModal} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent
          bg="#111111"
          border="1px solid rgba(255,255,255,0.10)"
          borderRadius="10px"
        >
          <ModalHeader color="#fafafa" fontWeight="600" letterSpacing="-0.01em">
            Tell us about the file(s) you're uploading
          </ModalHeader>
          <ModalCloseButton color="#a3a3a3" isDisabled={isUploading} />
          <ModalBody>
            <Text color="#a3a3a3" fontSize="sm" mb={3} lineHeight="1.7">
              Tell us about the file(s) you're uploading. Press Enter to submit, Shift+Enter for a new line.
            </Text>
            <Textarea
              value={uploadContext}
              onChange={(e) => setUploadContext(e.target.value)}
              onKeyDown={handleContextKeyDown}
              placeholder="Example: This is a list of SaaS founders from a conference attendee sheet..."
              bg="rgba(255,255,255,0.02)"
              border="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              color="#fafafa"
              _placeholder={{ color: "#a3a3a3" }}
              _hover={{ borderColor: "rgba(255,255,255,0.15)" }}
              _focus={{ borderColor: "rgba(255,255,255,0.3)" }}
              borderRadius="8px"
              minH="120px"
              autoFocus
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button {...outlineButton} onClick={closeContextModal} isDisabled={isUploading}>
              Cancel
            </Button>
            <Button
              {...primaryButton}
              onClick={submitUploadWithContext}
              isDisabled={!uploadContext.trim() || pendingFiles.length === 0 || isUploading}
              isLoading={isUploading}
            >
              Upload {pendingFiles.length > 1 ? `${pendingFiles.length} files` : 'file'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MessageInput;

